import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["input", "progress", "progressBar", "uploadText", "uploadIcon", "dropzone", "fileList", "overlay"]
    static values = {
        url: String,
        maxFileSize: { type: Number, default: 100 },
        maxFiles: { type: Number, default: 10 },
        dossierToken: String
    }

    connect() {
        this.boundDragEnter = this.dragenter.bind(this)
        this.boundDragOver = this.dragover.bind(this)
        this.boundDragLeave = this.dragleave.bind(this)
        this.boundDrop = this.drop.bind(this)

        document.addEventListener("dragenter", this.boundDragEnter)
        document.addEventListener("dragover", this.boundDragOver)
        document.addEventListener("dragleave", this.boundDragLeave)
        document.addEventListener("drop", this.boundDrop)

        this.dragCounter = 0
        this.currentUploads = 0
        this.totalUploads = 0
        this.uploadingFiles = new Map()
    }

    hasFiles(e) {
        if (!e.dataTransfer) return false
        const types = e.dataTransfer.types
        if (!types) return false
        return Array.from(types).includes("Files")
    }

    showOverlay() {
        if (this.hasOverlayTarget) {
            this.overlayTarget.classList.add('is-active')
        }
        if (this.hasDropzoneTarget) {
            this.dropzoneTarget.classList.add('drag-active')
        }
    }

    hideOverlay() {
        if (this.hasOverlayTarget) {
            this.overlayTarget.classList.remove('is-active')
        }
        if (this.hasDropzoneTarget) {
            this.dropzoneTarget.classList.remove('drag-active')
        }
    }

    dragenter(e) {
        if (!this.hasFiles(e)) return
        e.preventDefault()
        this.dragCounter++
        this.showOverlay()
    }

    dragover(e) {
        if (!this.hasFiles(e)) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
    }

    dragleave(e) {
        if (!this.hasFiles(e)) return
        e.preventDefault()
        this.dragCounter--
        if (this.dragCounter <= 0) {
            this.dragCounter = 0
            this.hideOverlay()
        }
    }

    drop(e) {
        if (!this.hasFiles(e)) return
        e.preventDefault()
        e.stopPropagation()
        this.dragCounter = 0
        this.hideOverlay()
        const files = e.dataTransfer.files
        this.handleFiles(files)
    }

    browse(e) {
        this.inputTarget.click()
    }

    handleChange(e) {
        const files = this.inputTarget.files
        this.handleFiles(files)
    }

    handleFiles(files) {
        if (files.length === 0) return

        // Check if adding these files would exceed the max files limit
        if (this.currentUploads + files.length > this.maxFilesValue) {
            alert(`Cannot upload more than ${this.maxFilesValue} files at once`)
            return
        }
        
        // Process each file individually
        Array.from(files).forEach(file => {
            if (this.validateFile(file)) {
                this.addFileToList(file)
                this.uploadFile(file)
            }
        })
    }

    validateFile(file) {
        // HEIC/HEIF: the server keeps the file and analyses a JPEG copy (BynnForensic::Document::HeicIntake).
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']

        if (!allowedTypes.includes(file.type)) {
            alert('File type not permitted! Allowed types: PDF, JPEG, PNG, HEIC, Excel, Word')
            return false
        }

        if (file.size > this.maxFileSizeValue * 1024 * 1024) {
            alert(`File size must be less than ${this.maxFileSizeValue}MB`)
            return false
        }

        return true
    }

    addFileToList(file) {
        const fileId = `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        const fileItem = document.createElement('div')
        fileItem.id = fileId
        fileItem.className = 'document-app__file-item'
        
        const fileIcon = this.getFileIcon(file.type)
        
        fileItem.innerHTML = `
            <div class="document-app__file-info">
                <i class="${fileIcon} document-app__file-icon"></i>
                <div class="document-app__file-details">
                    <div class="document-app__file-name">${file.name}</div>
                    <div class="document-app__file-size">${this.formatFileSize(file.size)}</div>
                </div>
            </div>
            <div class="document-app__file-progress">
                <div class="progress">
                    <div class="progress-bar" role="progressbar" style="width: 0%" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100"></div>
                </div>
                <div class="document-app__file-status">
                    <i class="fa-solid fa-circle-notch fa-spin document-app__file-status-icon"></i>
                    <span>Uploading...</span>
                </div>
            </div>
        `
        
        // Insert at the beginning of the list instead of appending to the end
        if (this.fileListTarget.firstChild) {
            this.fileListTarget.insertBefore(fileItem, this.fileListTarget.firstChild)
        } else {
            this.fileListTarget.appendChild(fileItem)
        }
        
        this.uploadingFiles.set(fileId, { file, element: fileItem })
        this.currentUploads++
        this.totalUploads++
    }

    getFileIcon(fileType) {
        if (fileType.includes('pdf')) return 'fa-solid fa-file-pdf'
        if (fileType.includes('image')) return 'fa-solid fa-file-image'
        if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'fa-solid fa-file-excel'
        if (fileType.includes('word') || fileType.includes('document')) return 'fa-solid fa-file-word'
        return 'fa-solid fa-file'
    }

    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    uploadFile(file) {
        const fileId = Array.from(this.uploadingFiles.entries())
            .find(([_, value]) => value.file === file)?.[0]
        
        if (!fileId) return

        const fileElement = this.uploadingFiles.get(fileId).element
        const progressBar = fileElement.querySelector('.progress-bar')
        const statusText = fileElement.querySelector('.document-app__file-status span')
        const statusIcon = fileElement.querySelector('.document-app__file-status-icon')

        const formData = new FormData()
        formData.append('document[file]', file)

        // Add dossier token if available
        if (this.hasDossierTokenValue && this.dossierTokenValue) {
            formData.append('document[dossier_token]', this.dossierTokenValue)
        }

        // Add fraud reasoning toggle state (explicit on/off)
        const fraudReasoningToggle = document.getElementById('fraud-reasoning-toggle')
        if (fraudReasoningToggle) {
            formData.append('document[use_orchestrators]', fraudReasoningToggle.checked ? 'true' : 'false')
        }

        const xhr = new XMLHttpRequest()

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
                const percentComplete = (e.loaded / e.total) * 100
                progressBar.style.width = percentComplete + '%'
                progressBar.setAttribute('aria-valuenow', percentComplete)
            }
        })

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                // Process successful response
                try {
                    const response = JSON.parse(xhr.responseText)
                    this.updateFileStatus(fileId, 'success', response.message || 'Upload successful')
                } catch (e) {
                    // Handle Turbo Stream responses
                    const parser = new DOMParser()
                    const html = parser.parseFromString(xhr.responseText, 'text/html')
                    const streamElements = html.querySelectorAll('turbo-stream')
                    streamElements.forEach(element => {
                        window.Turbo.renderStreamMessage(element.outerHTML)
                    })
                    this.updateFileStatus(fileId, 'success', 'Upload successful')
                }
            } else {
                try {
                    const response = JSON.parse(xhr.responseText)
                    this.updateFileStatus(fileId, 'error', response.error || 'Upload failed')
                } catch (e) {
                    this.updateFileStatus(fileId, 'error', 'Upload failed')
                }
            }
        })

        xhr.addEventListener('error', () => {
            this.updateFileStatus(fileId, 'error', 'Network error occurred')
        })

        xhr.open('POST', this.urlValue)
        xhr.setRequestHeader('X-CSRF-Token', document.querySelector('meta[name="csrf-token"]')?.content)
        xhr.setRequestHeader('Accept', 'application/json, text/vnd.turbo-stream.html, text/html')
        xhr.send(formData)
    }

    updateFileStatus(fileId, status, message) {
        const fileElement = this.uploadingFiles.get(fileId)?.element
        if (!fileElement) return

        const statusText = fileElement.querySelector('.document-app__file-status span')
        const statusIcon = fileElement.querySelector('.document-app__file-status-icon')

        statusText.textContent = message
        statusIcon.classList.remove('fa-circle-notch', 'fa-spin')

        if (status === 'success') {
            statusIcon.classList.add('fa-check-circle')
            statusIcon.style.color = '#198754'
            fileElement.classList.add('document-app__file-item--success')
        } else if (status === 'error') {
            statusIcon.classList.add('fa-exclamation-circle')
            statusIcon.style.color = '#dc3545'
            fileElement.classList.add('document-app__file-item--error')
        }

        this.currentUploads--
        if (status === 'success') {
            // Remove the file from the list after a delay
            setTimeout(() => {
                fileElement.classList.add('document-app__file-item--fade-out')
                setTimeout(() => {
                    this.uploadingFiles.delete(fileId)
                    fileElement.remove()
                }, 500)
            }, 3000)
        }
    }

    disconnect() {
        document.removeEventListener("dragenter", this.boundDragEnter)
        document.removeEventListener("dragover", this.boundDragOver)
        document.removeEventListener("dragleave", this.boundDragLeave)
        document.removeEventListener("drop", this.boundDrop)
    }
}